import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { DeliveryClassification } from "@src/utils/deliveryClassification";
import { AxiosError } from "axios";
import { ApiProvider } from "@src/store/ApiProvider";
import { handleError } from "@src/store/getThunkAndSlice";

export interface IPoInventoryItem {
	Event_ID: number;
	Event_Name: string;
	Event_Date: string;
	Event_Time: string;
	Venue_Name: string;
	Section: string;
	Row: string;
	First_Seat: string;
	Is_Odd_Even: boolean;
	Quantity: string;
	Cost: string;
	Face: number;
	Is_Available: boolean;
	Expected_Availability_Date: string;
	Is_eDelivery: string;
	Delivery_Classification: DeliveryClassification | null;
	Internal_Notes: string;
	External_Notes: string;
	Procurement_Notes: string;
	Tracking_Number: string;
	Confirmation_Number: string;
	Is_Error: string;
	Total: string;
	Vendor: string;
	Purchaser_Name: string;
	Credit_Card: string;
	Account_Name: string;
}

export interface ICreatePurchaseOrderParams {
	companyName: string | null;
	confirmationNumber: string;
	purchaserName: string;
	accountName: string;
	creditCard: string;
	vendor: string;
	inventory: Array<IPoInventoryItem>;
}

export interface ICreatePoResponse {
	PO_ID?: number | null;
	poNumber?: number | string | null;
	Id?: number | null;
}

export interface IDuplicateListing {
	Event_ID: number;
	Section: string;
	Row: string;
	First_Seat?: number;
	Quantity?: number;
}

export interface ICreatePoRejection {
	message: string;
	status?: number;
	duplicates?: Array<IDuplicateListing>;
}

export const createPurchaseOrder = createAsyncThunk<
	ICreatePoResponse,
	ICreatePurchaseOrderParams,
	{ rejectValue: ICreatePoRejection }
>("purchasing/createPurchaseOrder", async (params, { rejectWithValue }) => {
	try {
		const duplicates = await ApiProvider.default.post<Array<IDuplicateListing>>("/api/Purchasing/CheckPO", {
			companyName: params.companyName,
			inventory: params.inventory,
		});

		if (Array.isArray(duplicates) && duplicates.length) {
			return rejectWithValue({ message: "Tickets already exist in the system.", duplicates });
		}

		const created = await ApiProvider.default.post<ICreatePoResponse>("/api/Purchasing/CreateManualPO", {
			companyName: params.companyName,
			inventory: params.inventory,
			creditCard: params.creditCard,
			vendor: params.vendor,
			purchaserName: params.purchaserName,
			accountName: params.accountName,
			confirmationNumber: params.confirmationNumber,
		});

		return created;
	} catch (error) {
		return rejectWithValue({
			message: handleError(error),
			status: error instanceof AxiosError ? error.response?.status : undefined,
		});
	}
});

export const createPurchaseOrderSlice = createSlice({
	name: "purchasing/createPurchaseOrder",
	initialState: { loading: false, error: null as string | null },
	reducers: {},
	extraReducers: (builder) => {
		builder
			.addCase(createPurchaseOrder.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(createPurchaseOrder.fulfilled, (state) => {
				state.loading = false;
			})
			.addCase(createPurchaseOrder.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload?.message ?? null;
			});
	},
});
